import type { PluginInfo, SidecarInfo } from "@magic-md/engine-client";
import {
    resolveSidecarManifest,
    type SidecarManifest,
} from "@magic-md/engine-client/protocol";
import type {
    ConfigOwner,
    ConfigTarget,
    SettingsRegistryEntry,
    SettingsSnapshot,
} from "@magic-md/engine-client/config";
import { pluginUIRegistry } from "../../../plugins/plugin-ui-registry";
import {
    getPluginSourceSearchTerms,
    pluginMatchesSourceFilter,
    type PluginSourceFilter,
} from "../../../plugins/plugin-source-metadata";
import {
    filterExtensionListItemsBySource,
    type ExtensionListItemModel,
    type ExtensionSourceFilter,
} from "../../ExtensionList";
import {
    filterSidecarListItemsBySource,
    type SidecarListItemModel,
    type SidecarSourceFilter,
} from "../../SidecarList";
import type { SettingsSection } from "../../../store/settings-store";
import { SECTION_LABELS } from "./constants";
import {
    settingsEntryDescription,
    settingsEntryTitle,
    type SettingsTextResolver,
} from "./localization";
import {
    getSchemaProperties,
    hasSchemaProperties,
    schemaPrimaryType,
} from "./schema-helpers";
import type {
    EntryListGroup,
    SettingGroup,
    SettingGroupCatalogEntry,
} from "./types";

const CN_ENABLED_SUFFIX_RE = / \u5df2\u542f\u7528$/;
const CN_SETTINGS_SUFFIX_RE = / \u8bbe\u7f6e$/;
const CN_DEFAULTS_SUFFIX_RE = / \u9ed8\u8ba4\u5b9e\u4f8b\u914d\u7f6e$/;

export function buildLocalPluginCatalog(): Record<
    string,
    SettingGroupCatalogEntry
> {
    const catalog: Record<string, SettingGroupCatalogEntry> = {};
    for (const [name, plugin] of pluginUIRegistry.getAll()) {
        catalog[name] = {
            icon: plugin.manifest.icon,
            title: plugin.manifest.title ?? plugin.manifest.name,
            description: plugin.manifest.description ?? plugin.manifest.overview,
            version: plugin.source?.version ?? plugin.manifest.version,
            actionCount: plugin.manifest.actions.length,
            source: plugin.source,
        };
    }
    return catalog;
}

export function buildPluginCatalog(
    plugins: PluginInfo[],
): Record<string, SettingGroupCatalogEntry> {
    return Object.fromEntries(
        plugins.map((plugin) => [
            plugin.name,
            {
                icon: plugin.icon,
                title: plugin.title ?? plugin.name,
                description: plugin.description ?? plugin.overview,
                version: plugin.source?.version ?? plugin.version,
                actionCount: plugin.actions?.length ?? 0,
                source: plugin.source,
            } satisfies SettingGroupCatalogEntry,
        ]),
    );
}

export function buildSidecarCatalog(
    sidecars: SidecarInfo[],
    locale = "zh-CN",
    fallbackLocale = "zh-CN",
): Record<string, SettingGroupCatalogEntry> {
    return Object.fromEntries(
        sidecars.map((sidecar) => {
            const manifest = sidecar.manifest
                ? resolveSidecarManifest(
                    sidecar.manifest as unknown as Record<string, unknown>,
                    undefined,
                    { locale, fallbackLocale },
                ) as unknown as SidecarManifest
                : null;
            return [
                sidecar.id,
                {
                    icon: manifest?.icon,
                    title: manifest?.title ?? manifest?.name ?? sidecar.id,
                    description:
                        manifest?.description ?? manifest?.overview,
                    sidecarSourceKind: sidecar.sourceKind ?? manifest?.sourceKind,
                    sidecarTransportType:
                        sidecar.transport?.type ?? manifest?.transport?.type,
                    alive: sidecar.alive,
                } satisfies SettingGroupCatalogEntry,
            ];
        }),
    );
}

export function mergeCatalogMaps(
    ...maps: Array<Record<string, SettingGroupCatalogEntry>>
): Record<string, SettingGroupCatalogEntry> {
    const merged: Record<string, SettingGroupCatalogEntry> = {};
    for (const map of maps) {
        for (const [key, value] of Object.entries(map)) {
            merged[key] = {
                ...merged[key],
                ...value,
            };
        }
    }
    return merged;
}

export function buildVisibleSections(
    entries: SettingsRegistryEntry[],
    capabilities: SettingsSnapshot["capabilities"],
    target?: ConfigTarget,
): SettingsSection[] {
    const sections = new Set<SettingsSection>();
    for (const entry of entries) {
        const ownerCapability = capabilities.owners[entry.owner];
        const readableTargets = ownerCapability?.readableTargets ?? [];
        const writableTargets = ownerCapability?.writableTargets ?? [];
        if (target && !entry.targets.includes(target)) {
            continue;
        }
        if (
            target &&
            target !== "user" &&
            !readableTargets.includes(target) &&
            !writableTargets.includes(target)
        ) {
            continue;
        }
        if (
            !target &&
            !entry.targets.some((candidateTarget) => {
                if (candidateTarget === "user") {
                    return capabilities.availableTargets.includes("user");
                }
                return (
                    capabilities.availableTargets.includes(candidateTarget) &&
                    (readableTargets.includes(candidateTarget) ||
                        writableTargets.includes(candidateTarget))
                );
            })
        ) {
            continue;
        }
        sections.add(sectionForOwner(entry.owner));
    }
    if (sections.size === 0) {
        sections.add("editor");
    }
    return [...sections];
}

export function buildSettingGroups(
    entries: SettingsRegistryEntry[],
    owner: "plugin" | "sidecar" | "extension",
    catalog: Record<string, SettingGroupCatalogEntry>,
): SettingGroup[] {
    const groups = new Map<string, SettingGroup>();

    for (const entry of entries) {
        if (entry.owner !== owner) continue;
        const id = extractGroupId(entry.key, owner);
        if (!id) continue;
        const metadata = catalog[id];
        const group = groups.get(id) ?? {
            id,
            icon: metadata?.icon ?? groupIcon(owner),
            title: groupTitle(id, metadata, entry),
            description: metadata?.description,
            summary: groupSummary(owner, metadata),
            source: metadata?.source,
            version: metadata?.version,
            category: metadata?.category,
            actionCount: metadata?.actionCount,
            sidecarSourceKind: metadata?.sidecarSourceKind,
            sidecarTransportType: metadata?.sidecarTransportType,
            extensionRuntimeType: metadata?.extensionRuntimeType,
            extensionSource: metadata?.extensionSource,
            alive: metadata?.alive,
            entries: [],
        };
        group.entries.push(entry);
        if (entry.key.endsWith(".enabled")) {
            group.enabledEntry = entry;
        }
        group.icon = metadata?.icon ?? group.icon;
        group.title = metadata?.title ?? group.title;
        group.description = metadata?.description ?? group.description;
        group.summary = metadata?.description ?? group.summary;
        group.source = metadata?.source ?? group.source;
        group.version = metadata?.version ?? group.version;
        group.category = metadata?.category ?? group.category;
        group.actionCount = metadata?.actionCount ?? group.actionCount;
        group.extensionRuntimeType =
            metadata?.extensionRuntimeType ?? group.extensionRuntimeType;
        group.extensionSource = metadata?.extensionSource ?? group.extensionSource;
        group.sidecarSourceKind = metadata?.sidecarSourceKind ?? group.sidecarSourceKind;
        group.sidecarTransportType =
            metadata?.sidecarTransportType ?? group.sidecarTransportType;
        group.alive = metadata?.alive ?? group.alive;
        groups.set(id, group);
    }

    return [...groups.values()]
        .map((group) => ({
            ...group,
            entries: sortGroupedEntries(group.entries),
        }))
        .sort((left, right) => left.title.localeCompare(right.title));
}

export function filterSettingGroups(
    groups: SettingGroup[],
    search: string,
    resolveText?: SettingsTextResolver,
): SettingGroup[] {
    if (!search) return groups;
    return groups.filter(
        (group) =>
            [group.id, resolveSearchText(group.title, resolveText), resolveSearchText(group.summary, resolveText)]
                .concat(group.version ? [group.version] : [])
                .concat(group.actionCount == null ? [] : [String(group.actionCount)])
                .concat(group.sidecarSourceKind ? [group.sidecarSourceKind] : [])
                .concat(group.sidecarTransportType ? [group.sidecarTransportType] : [])
                .concat(getPluginSourceSearchTerms(group.source))
                .concat(getExtensionSourceSearchTerms(group))
                .filter(Boolean)
                .some((value) => value?.toLowerCase().includes(search)) ||
            group.entries.some((entry) => matchesSearch(entry, search, resolveText)),
    );
}

export function filterSettingGroupsBySource(
    groups: SettingGroup[],
    sourceFilter: PluginSourceFilter,
): SettingGroup[] {
    return groups.filter((group) => pluginMatchesSourceFilter(group.source, sourceFilter));
}

export function filterSettingGroupsBySidecarSource(
    groups: SettingGroup[],
    sourceFilter: SidecarSourceFilter,
): SettingGroup[] {
    const items = groups.map((group) => sidecarGroupToListItem(group));
    const visibleIds = new Set(
        filterSidecarListItemsBySource(items, sourceFilter).map((item) => item.id),
    );
    return groups.filter((group) => visibleIds.has(group.id));
}

export function filterSettingGroupsByExtensionSource(
    groups: SettingGroup[],
    sourceFilter: ExtensionSourceFilter,
): SettingGroup[] {
    const items = groups.map((group) => extensionGroupToListItem(group));
    const visibleIds = new Set(
        filterExtensionListItemsBySource(items, sourceFilter).map((item) => item.id),
    );
    return groups.filter((group) => visibleIds.has(group.id));
}

function sidecarGroupToListItem(group: SettingGroup): SidecarListItemModel {
    return {
        id: group.id,
        title: group.title,
        icon: group.icon,
        description: group.description ?? group.summary,
        alive: group.alive,
        sourceKind: group.sidecarSourceKind,
        transportType: group.sidecarTransportType,
    };
}

function extensionGroupToListItem(group: SettingGroup): ExtensionListItemModel {
    return {
        id: group.id,
        title: group.title,
        icon: group.icon,
        description: group.description ?? group.summary,
        category: group.category,
        version: group.version,
        runtimeType: group.extensionRuntimeType,
        source: group.extensionSource,
    };
}

export function buildEntryListGroups(
    section: SettingsSection,
    entries: SettingsRegistryEntry[],
): EntryListGroup[] {
    const groups = new Map<string, EntryListGroup>();

    for (const entry of entries) {
        const group = entryListGroupForEntry(section, entry);
        const existing = groups.get(group.id) ?? {
            ...group,
            entries: [],
        };
        existing.entries.push(entry);
        groups.set(group.id, existing);
    }

    return [...groups.values()];
}

export function matchesSearch(
    entry: SettingsRegistryEntry,
    search: string,
    resolveText?: SettingsTextResolver,
): boolean {
    if (!search) return true;
    return [
        entry.key,
        resolveText
            ? settingsEntryTitle(entry, resolveText)
            : resolveSearchText(entry.title),
        resolveText
            ? settingsEntryDescription(entry, resolveText)
            : resolveSearchText(entry.description),
        entry.owner,
    ]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(search));
}

export function countDetailFields(entries: SettingsRegistryEntry[]): number {
    return entries.reduce((count, entry) => {
        const rawType = schemaPrimaryType(entry.schema);
        if (rawType === "object" && hasSchemaProperties(entry.schema)) {
            return count + getSchemaProperties(entry.schema).length;
        }
        return count + 1;
    }, 0);
}

export function resolvePrimaryDetailEntry(
    group: SettingGroup,
    entries: SettingsRegistryEntry[],
): SettingsRegistryEntry | undefined {
    const settingsKeyCandidates = [
        `plugins.${group.id}.settings`,
        `sidecars.${group.id}.settings`,
        `extensions.${group.id}.settings`,
    ];
    const settingsEntry = entries.find((entry) =>
        settingsKeyCandidates.includes(entry.key),
    );
    if (settingsEntry) {
        return settingsEntry;
    }
    return entries.length === 1 ? entries[0] : undefined;
}

// --- internal helpers ---

function extractGroupId(
    key: string,
    owner: "plugin" | "sidecar" | "extension",
): string | null {
    if (owner === "plugin") {
        const match = key.match(/^plugins\.([^.]+)\./);
        return match?.[1] ?? null;
    }
    if (owner === "sidecar") {
        const match = key.match(/^sidecars\.([^.]+)\./);
        return match?.[1] ?? null;
    }
    const match = key.match(/^extensions\.([^.]+)\./);
    return match?.[1] ?? null;
}

function sortGroupedEntries(
    entries: SettingsRegistryEntry[],
): SettingsRegistryEntry[] {
    const order = (entry: SettingsRegistryEntry) => {
        if (entry.key.endsWith(".enabled")) return 0;
        if (entry.key.endsWith(".settings")) return 1;
        if (entry.key.endsWith(".defaults")) return 2;
        return 3;
    };
    return [...entries].sort((left, right) => order(left) - order(right));
}

function deriveGroupTitle(entry: SettingsRegistryEntry): string {
    const title = entry.title ?? entry.key;
    return title
        .replace(CN_ENABLED_SUFFIX_RE, "")
        .replace(CN_SETTINGS_SUFFIX_RE, "")
        .replace(CN_DEFAULTS_SUFFIX_RE, "");
}

function groupTitle(
    id: string,
    metadata: SettingGroupCatalogEntry | undefined,
    entry: SettingsRegistryEntry,
): string {
    return metadata?.title ?? deriveGroupTitle(entry) ?? id;
}

function groupSummary(
    owner: "plugin" | "sidecar" | "extension",
    metadata?: SettingGroupCatalogEntry,
): string {
    if (metadata?.description) {
        return metadata.description;
    }
    if (owner === "plugin") return "%editor.settings.groupSummary.plugin%";
    if (owner === "sidecar") return "%editor.settings.groupSummary.sidecar%";
    return "%editor.settings.groupSummary.extension%";
}

function groupIcon(owner: "plugin" | "sidecar" | "extension"): string {
    if (owner === "plugin") return "🧩";
    if (owner === "sidecar") return "⚙";
    return "🧩";
}

function getExtensionSourceSearchTerms(group: SettingGroup): string[] {
    return [
        group.category,
        group.extensionRuntimeType,
        group.extensionSource?.distributionKind,
        group.extensionSource?.manifestUrl,
        group.extensionSource?.uiBundleUrl,
        group.extensionSource?.invokeUrl,
        group.extensionSource?.localPath,
        group.extensionSource?.manifestPath,
        group.extensionSource?.uiBundlePath,
    ].filter((value): value is string => !!value);
}

function entryListGroupForEntry(
    section: SettingsSection,
    entry: SettingsRegistryEntry,
): Omit<EntryListGroup, "entries"> {
    if (section === "editor") {
        if (entry.key.startsWith("editor.appearance.")) {
            return { id: "appearance", title: "%editor.settings.group.appearance%" };
        }
        if (
            entry.key.startsWith("editor.preview.") ||
            entry.key.startsWith("editor.publish.")
        ) {
            return {
                id: "content-presentation",
                title: "%editor.settings.group.contentPresentation%",
            };
        }
        if (entry.key.startsWith("editor.codeMirror.")) {
            return { id: "code-mirror", title: "CodeMirror" };
        }
        return { id: "editor-general", title: "%editor.settings.group.general%" };
    }

    if (section === "runtime") {
        if (entry.owner === "gateway" || entry.key.startsWith("gateway.")) {
            return { id: "gateway", title: "Gateway" };
        }
        return { id: "engine", title: "Engine" };
    }

    return { id: section, title: SECTION_LABELS[section] };
}

function resolveSearchText(
    value: string | null | undefined,
    resolveText?: SettingsTextResolver,
): string | undefined {
    if (!value) return undefined;
    return resolveText ? resolveText(value) : value;
}

export function sectionForOwner(owner: ConfigOwner): SettingsSection {
    switch (owner) {
        case "plugin":
            return "plugin";
        case "sidecar":
            return "sidecar";
        case "engine":
        case "gateway":
            return "runtime";
        default:
            return "editor";
    }
}
